import { StoreApi } from 'zustand';

import { History } from '@spazious/dollhouse-history';
import { FShallowGraph, GraphAction, graphReducer } from '@spazious/dollhouse-shallow-graph';
import { StagingArea } from '@spazious/dollhouse-staging-area';

import { CommanderActions } from './actions/index.js';
import { ActionArgsByType, createActionFromType, historyBuildGraph } from './utils/index.js';
import {
  ActionFromType,
  ActionSideEffectsHandlers,
  Commander,
  CommanderAction,
  CommanderActionTypesEnum,
} from './types.js';

export class CommanderImpl<
  TState extends FShallowGraph = FShallowGraph,
  TAction extends CommanderAction = CommanderActions,
  TMeta = CommanderActionTypesEnum
> implements Commander<TState, TAction, TMeta>
{
  store: StoreApi<TState>;
  history: History<GraphAction[], TMeta>;
  staging: StagingArea<GraphAction, TMeta>;
  sideEffectHandlers?: ActionSideEffectsHandlers;
  prevState: TState;

  constructor(
    store: StoreApi<TState>,
    history: History<GraphAction[], TMeta>,
    staging: StagingArea<GraphAction, TMeta>,
    sideEffectHandlers?: ActionSideEffectsHandlers
  ) {
    this.store = store;
    this.history = history;
    this.staging = staging;
    this.sideEffectHandlers = sideEffectHandlers;
    this.prevState = store.getState();

    const updateStateFromHistory = () => {
      const shallowGraph = historyBuildGraph(history.head);
      store.setState(shallowGraph as TState);
      this.prevState = store.getState();
    };

    staging.on('change', () => {
      const shallowGraph = staging.stack.reduce(graphReducer, this.prevState);
      store.setState(shallowGraph as TState);
    });

    staging.on('commit', (stack, meta) => {
      history.push(stack, meta);
    });

    history.on('change', () => {
      updateStateFromHistory();
    });

    updateStateFromHistory();
  }

  createAction<TType extends TAction['type']>(
    type: TType,
    ...args: ActionArgsByType<TType>
  ): ActionFromType<TAction, TType> {
    return createActionFromType(this as Commander, type, ...args);
  }
}

export const createCommander = <
  TState extends FShallowGraph = FShallowGraph,
  TMeta extends CommanderActionTypesEnum = CommanderActionTypesEnum
>(
  store: StoreApi<TState>,
  history: History<GraphAction[], TMeta>,
  staging: StagingArea<GraphAction, TMeta>,
  sideEffectHandlers?: ActionSideEffectsHandlers
) => {
  return new CommanderImpl(store, history, staging, sideEffectHandlers);
};
